import { useEffect, useState, useRef } from "react";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  getDatasets, uploadDataset, deleteDataset,
  getYouTubeTranscripts, extractYouTubeTranscript, deleteYouTubeTranscript,
  uploadTextDataset, getIndexedDatasets, getVerticals
} from "@/lib/api";
import { Upload, Trash2, Loader2, FileJson, Youtube, Play, AlertCircle, CheckCircle2, FileText, Building2, Database } from "lucide-react";
import { toast } from "sonner";

export default function AdminDatasets() {
  const [datasets, setDatasets] = useState([]);
  const [transcripts, setTranscripts] = useState({ ai_consultant: [], sales_coach: [] });
  const [verticalDatasets, setVerticalDatasets] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [selectedVertical, setSelectedVertical] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadingText, setUploadingText] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [targetKb, setTargetKb] = useState("ai_consultant");
  const [textTargetKb, setTextTargetKb] = useState("ai_consultant");
  const fileInputRef = useRef(null);
  const textFileInputRef = useRef(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [datasetsRes, consultantTranscriptsRes, salesTranscriptsRes, indexedRes, verticalsRes] = await Promise.all([
        getDatasets(),
        getYouTubeTranscripts("ai_consultant"),
        getYouTubeTranscripts("sales_coach"),
        getIndexedDatasets(),
        getVerticals()
      ]);
      setDatasets(datasetsRes.data || []);
      setTranscripts({
        ai_consultant: consultantTranscriptsRes.data || [],
        sales_coach: salesTranscriptsRes.data || []
      });
      setVerticalDatasets(indexedRes.data?.filter(d => d.vertical_id) || []);
      setVerticals(verticalsRes.data || []);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      toast.error("Please upload a JSON file");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await uploadDataset(formData);
      
      if (response.data.success) {
        toast.success(response.data.message);
        fetchData();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error("Failed to upload dataset");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDeleteDataset = async (filename) => {
    if (!window.confirm(`Delete dataset "${filename}"? This cannot be undone.`)) return;
    
    try {
      await deleteDataset(filename);
      toast.success("Dataset deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete dataset");
    }
  };

  const handleTextFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.txt')) {
      toast.error("Please upload a .txt file");
      return;
    }

    setUploadingText(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("target_kb", textTargetKb);
      
      const response = await uploadTextDataset(formData);
      
      if (response.data.success) {
        toast.success(response.data.message);
        fetchData();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error("Failed to upload text file");
    } finally {
      setUploadingText(false);
      if (textFileInputRef.current) {
        textFileInputRef.current.value = "";
      }
    }
  };

  const handleExtractTranscript = async () => {
    if (!youtubeUrl.trim()) {
      toast.error("Please enter a YouTube URL");
      return;
    }

    setExtracting(true);
    try {
      const response = await extractYouTubeTranscript({
        youtube_url: youtubeUrl,
        target_kb: targetKb
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        setYoutubeUrl("");
        fetchData();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error("Failed to extract transcript");
    } finally {
      setExtracting(false);
    }
  };

  const handleDeleteTranscript = async (docId, kb) => {
    if (!window.confirm("Delete this transcript?")) return;
    
    try {
      await deleteYouTubeTranscript(docId, kb);
      toast.success("Transcript deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete transcript");
    }
  };

  return (
    <AdminLayout title="AI Datasets & Training">
      <div className="space-y-6" data-testid="admin-datasets">
        <p className="text-slate-600">Upload JSON datasets, extract YouTube content, and manage vertical-based datasets for AI training</p>

        <Tabs defaultValue="datasets" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 max-w-xl">
            <TabsTrigger value="datasets" className="flex items-center gap-2">
              <FileJson className="h-4 w-4" />
              JSON Datasets
            </TabsTrigger>
            <TabsTrigger value="youtube" className="flex items-center gap-2">
              <Youtube className="h-4 w-4" />
              YouTube Transcripts
            </TabsTrigger>
            <TabsTrigger value="vertical" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              From Vertical
            </TabsTrigger>
          </TabsList>

          {/* JSON Datasets Tab */}
          <TabsContent value="datasets" className="space-y-6">
            {/* Upload Section - Two Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* JSON Upload */}
              <Card className="border-slate-200 border-dashed">
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FileJson className="h-6 w-6 text-blue-600" />
                    </div>
                    <h3 className="font-medium text-slate-900 mb-2">Upload JSON Dataset</h3>
                    <p className="text-sm text-slate-500 mb-4">
                      Upload structured JSON files with knowledge items.
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".json"
                      onChange={handleFileUpload}
                      className="hidden"
                      data-testid="dataset-file-input"
                    />
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {uploading ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="h-4 w-4 mr-2" />
                      )}
                      {uploading ? "Uploading..." : "Select JSON File"}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Text File Upload - Converts to JSON */}
              <Card className="border-slate-200 border-dashed border-green-200 bg-green-50/30">
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FileText className="h-6 w-6 text-green-600" />
                    </div>
                    <h3 className="font-medium text-slate-900 mb-2">Upload Text Dataset</h3>
                    <p className="text-sm text-slate-500 mb-3">
                      Upload .txt files - auto-converted to JSON format.
                    </p>
                    <div className="mb-3">
                      <select
                        value={textTargetKb}
                        onChange={(e) => setTextTargetKb(e.target.value)}
                        className="w-full max-w-[200px] h-9 px-3 border border-slate-200 rounded-md text-sm"
                      >
                        <option value="ai_consultant">AI Consultant</option>
                        <option value="sales_coach">Sales Coach</option>
                      </select>
                    </div>
                    <input
                      ref={textFileInputRef}
                      type="file"
                      accept=".txt"
                      onChange={handleTextFileUpload}
                      className="hidden"
                      data-testid="text-dataset-file-input"
                    />
                    <Button
                      onClick={() => textFileInputRef.current?.click()}
                      disabled={uploadingText}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {uploadingText ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="h-4 w-4 mr-2" />
                      )}
                      {uploadingText ? "Converting..." : "Select Text File"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Datasets List */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg">Uploaded Datasets</CardTitle>
                <CardDescription>JSON files loaded into AI knowledge base</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Dataset</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Target Agent</TableHead>
                      <TableHead>Last Updated</TableHead>
                      <TableHead className="w-20">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8">
                          <Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" />
                        </TableCell>
                      </TableRow>
                    ) : datasets.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                          No datasets uploaded yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      datasets.map((dataset) => (
                        <TableRow key={dataset.filename}>
                          <TableCell>
                            <div>
                              <p className="font-medium text-sm">{dataset.title || dataset.dataset_key}</p>
                              <p className="text-xs text-slate-500">{dataset.filename}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded-full text-xs">
                              {dataset.category}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              dataset.target_agent?.includes("Sales") 
                                ? "bg-green-100 text-green-700" 
                                : "bg-blue-100 text-blue-700"
                            }`}>
                              {dataset.target_agent}
                            </span>
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {dataset.last_updated}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteDataset(dataset.filename)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* YouTube Transcripts Tab */}
          <TabsContent value="youtube" className="space-y-6">
            {/* Extract Section */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Youtube className="h-5 w-5 text-red-500" />
                  Extract YouTube Transcript
                </CardTitle>
                <CardDescription>
                  Enter a YouTube URL to extract its transcript and add to the AI knowledge base
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="md:col-span-2 space-y-2">
                    <Label className="text-xs">YouTube URL</Label>
                    <Input
                      value={youtubeUrl}
                      onChange={(e) => setYoutubeUrl(e.target.value)}
                      placeholder="https://www.youtube.com/watch?v=..."
                      data-testid="youtube-url-extract-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Target Knowledge Base</Label>
                    <select
                      value={targetKb}
                      onChange={(e) => setTargetKb(e.target.value)}
                      className="w-full h-10 px-3 border border-slate-200 rounded-md text-sm"
                    >
                      <option value="ai_consultant">AI Consultant</option>
                      <option value="sales_coach">Sales Coach</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    <Button
                      onClick={handleExtractTranscript}
                      disabled={extracting || !youtubeUrl.trim()}
                      className="w-full bg-red-600 hover:bg-red-700"
                    >
                      {extracting ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Play className="h-4 w-4 mr-2" />
                      )}
                      {extracting ? "Extracting..." : "Extract"}
                    </Button>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5" />
                    <div className="text-sm text-amber-700">
                      <p className="font-medium">Note</p>
                      <p>Only videos with available captions/subtitles can be transcribed. Auto-generated captions are supported.</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* AI Consultant Transcripts */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                  AI Consultant Transcripts
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Video ID</TableHead>
                      <TableHead>Upload Date</TableHead>
                      <TableHead className="w-20">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transcripts.ai_consultant.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center py-6 text-slate-500">
                          No transcripts extracted yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      transcripts.ai_consultant.map((t) => (
                        <TableRow key={t.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Youtube className="h-4 w-4 text-red-500" />
                              <a 
                                href={t.video_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline"
                              >
                                {t.video_id}
                              </a>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {new Date(t.upload_date).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteTranscript(t.id, "ai_consultant")}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Sales Coach Transcripts */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-green-500"></span>
                  Sales Coach Transcripts
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Video ID</TableHead>
                      <TableHead>Upload Date</TableHead>
                      <TableHead className="w-20">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transcripts.sales_coach.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center py-6 text-slate-500">
                          No transcripts extracted yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      transcripts.sales_coach.map((t) => (
                        <TableRow key={t.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Youtube className="h-4 w-4 text-red-500" />
                              <a 
                                href={t.video_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline"
                              >
                                {t.video_id}
                              </a>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {new Date(t.upload_date).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteTranscript(t.id, "sales_coach")}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* From Vertical Tab */}
          <TabsContent value="vertical" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Vertical-Based Datasets
                </CardTitle>
                <CardDescription>
                  Datasets auto-generated from vertical data (Testimonials, FAQs, Deliverables, ROI, etc.)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4 mb-4">
                  <Label className="text-sm font-medium">Filter by Vertical:</Label>
                  <select
                    value={selectedVertical}
                    onChange={(e) => setSelectedVertical(e.target.value)}
                    className="h-9 px-3 border border-slate-200 rounded-md text-sm"
                  >
                    <option value="">All Verticals</option>
                    {verticals.map(v => (
                      <option key={v.id} value={v.id}>{v.name}</option>
                    ))}
                  </select>
                </div>

                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Dataset Type</TableHead>
                      <TableHead>Vertical</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Items</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-8">
                          <Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" />
                        </TableCell>
                      </TableRow>
                    ) : verticalDatasets.filter(d => !selectedVertical || d.vertical_id === selectedVertical).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                          <Database className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                          <p>No vertical datasets found.</p>
                          <p className="text-xs mt-1">Go to AI Consultant KB and click Reindex Datasets to generate.</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      verticalDatasets
                        .filter(d => !selectedVertical || d.vertical_id === selectedVertical)
                        .map((dataset) => (
                          <TableRow key={dataset.dataset_id}>
                            <TableCell>
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                dataset.dataset_type?.includes('testimonial') ? 'bg-green-100 text-green-700' :
                                dataset.dataset_type?.includes('faq') ? 'bg-blue-100 text-blue-700' :
                                dataset.dataset_type?.includes('deliverable') ? 'bg-cyan-100 text-cyan-700' :
                                dataset.dataset_type?.includes('roi') ? 'bg-yellow-100 text-yellow-700' :
                                dataset.dataset_type?.includes('competitor') ? 'bg-red-100 text-red-700' :
                                dataset.dataset_type?.includes('timeline') ? 'bg-purple-100 text-purple-700' :
                                'bg-slate-100 text-slate-700'
                              }`}>
                                {dataset.dataset_type?.replace('vertical_', '').replace('_', ' ').toUpperCase()}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm font-medium">{dataset.vertical_name}</span>
                            </TableCell>
                            <TableCell className="text-sm text-slate-500">{dataset.source}</TableCell>
                            <TableCell>
                              <span className="text-sm font-medium">{dataset.item_count}</span>
                            </TableCell>
                          </TableRow>
                        ))
                    )}
                  </TableBody>
                </Table>

                <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-blue-800">How to update vertical datasets?</p>
                      <p className="text-sm text-blue-700 mt-1">
                        1. Edit vertical data in Verticals section (add testimonials, FAQs, deliverables, etc.)<br />
                        2. Go to AI Consultant KB → Click Reindex Datasets<br />
                        3. Datasets will be automatically regenerated from vertical data
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
